/**
 * useSecurityEvents — Shared WebSocket hook for real-time security event streaming.
 *
 * Uses a singleton module-level connection so all components sharing this hook
 * use a single WebSocket to the API Gateway's /ws/events endpoint.
 *
 * Features:
 *  - Single shared connection for the entire app (no duplicate connections)
 *  - Auto-reconnect with exponential back-off (max 30 s)
 *  - Heartbeat / ping-pong keep-alive
 *  - Subscriber pattern — each hook call registers as a listener
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { API_CONFIG } from '../services/api/config';
import { apiClient } from '../services/api/client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type SecurityEventType =
  | 'finding.created'
  | 'finding.normalized'
  | 'issue.created'
  | 'issue.updated'
  // R193-SHIP-AM: Wiz-parity lifecycle verbs the gateway now forwards.
  | 'issue.reopened'
  | 'issue.escalated'
  | 'issue.escalation_fired'
  // R193-SHIP-AN: remaining 5 lifecycle verbs the engine publishes and the
  // gateway now binds (resolve / suppress / snooze / assign + manual
  // severity / priority overrides). Without these the FE only saw
  // creation + reopen + escalation — operator actions were silent until
  // the user manually refreshed.
  | 'issue.resolved'
  | 'issue.suppressed'
  | 'issue.snoozed'
  // R193-SHIP-BE: snooze auto-expiry. Emitted by issue-engine's
  // _sla_breach_checker when a row's snoozed_until passes — the loop
  // flips status snoozed -> open and now publishes `issue.unsnoozed`
  // (previously only an issue_activity audit row was written, so the
  // FE had no way to know a snoozed issue had auto-resurfaced).
  // Light toast, NOT a P0/P1 urgent alert: the issue was already
  // triaged at snooze-time so this is an informational nudge.
  | 'issue.unsnoozed'
  | 'issue.assigned'
  | 'issue.severity_changed'
  | 'issue.priority_changed'
  // R193-SHIP-AV: dedicated SLA-breach routing key. Issue-engine's
  // _sla_breach_checker publishes `sla.breached` when an issue passes its
  // sla_deadline + status NOT IN (resolved/auto_resolved/suppressed); the
  // gateway WS forwards it tenant-scoped and TopNav renders a red urgent
  // toast.
  | 'sla.breached'
  | 'scan.started'
  | 'scan.progress'
  | 'scan.completed'
  | 'toxic_combination.detected'
  | 'runtime.alert'
  | 'remediation.completed'
  | 'compliance.change'
  | 'anomaly.detected'
  | 'graph.changed'
  | 'heartbeat'
  | 'ping'
  | 'pong'
  | 'connected';

export interface SecurityEvent {
  id: string;
  type: SecurityEventType;
  ts: string;
  /** Human-readable summary */
  title: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  /** Original raw payload from WS */
  raw: Record<string, unknown>;
}

// Maximum events kept in the rolling buffer
const MAX_EVENTS = 50;
// R193-SHIP-AM: per-task spec — 1s -> 2s -> 5s -> 30s cap. Indexed by _failCount.
// Subsequent failures reuse the last value.
const BACKOFF_SCHEDULE_MS = [1_000, 2_000, 5_000, 30_000];
const BASE_BACKOFF_MS = BACKOFF_SCHEDULE_MS[0];
const MAX_BACKOFF_MS = 30_000;
// After this many consecutive failures, slow down to IDLE_BACKOFF_MS to stop console spam
const MAX_FAIL_COUNT = 5;
const IDLE_BACKOFF_MS = 5 * 60_000; // 5 minutes

// ─── Singleton connection ─────────────────────────────────────────────────────

type Listener = (event: SecurityEvent) => void;
type ConnectedListener = (connected: boolean) => void;

let _ws: WebSocket | null = null;
let _isConnected = false;
let _backoff = BASE_BACKOFF_MS;
let _failCount = 0;
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
// R193-SHIP-AM: Page Visibility — pause reconnects while tab is hidden so
// background tabs don't pile up reconnect attempts when the user comes back.
let _tabHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
// R133: set when a tokenless connect attempt was rejected — i.e. this deployment
// runs the gateway with WS_AUTH_REQUIRED. While it is set and no token exists we
// stop reconnecting entirely (see _connect), because retrying can only 403 again.
// Cleared by _onAuthChange the moment a token appears.
let _awaitingAuth = false;
const _eventListeners = new Set<Listener>();
const _connectedListeners = new Set<ConnectedListener>();
const _eventBuffer: SecurityEvent[] = [];

function _broadcast(event: SecurityEvent) {
  _eventBuffer.unshift(event);
  if (_eventBuffer.length > MAX_EVENTS) _eventBuffer.length = MAX_EVENTS;
  _eventListeners.forEach(fn => fn(event));
}

function _broadcastConnected(connected: boolean) {
  _isConnected = connected;
  _connectedListeners.forEach(fn => fn(connected));
}

function _parseMessage(data: string): SecurityEvent | null {
  try {
    const raw = JSON.parse(data) as Record<string, unknown>;
    const type = (raw.type as SecurityEventType | undefined) || 'finding.created';
    // R193-SHIP-AM: 'ping' is handled at the onmessage layer (sends pong back)
    // before reaching here, but defensively swallow it too.
    if (type === 'heartbeat' || type === 'ping' || type === 'pong' || type === 'connected') return null;

    const sev = (raw.severity as string | undefined)?.toLowerCase();
    const pri = (raw.priority as string | undefined)?.toUpperCase();
    let severity: SecurityEvent['severity'] | undefined;
    if (sev === 'critical' || sev === 'high' || sev === 'medium' || sev === 'low' || sev === 'info') {
      severity = sev;
    } else if (pri === 'P0') severity = 'critical';
    else if (pri === 'P1') severity = 'high';
    else if (pri === 'P2') severity = 'medium';
    else if (pri === 'P3') severity = 'low';

    let title = '';
    switch (type) {
      case 'finding.created':
      case 'finding.normalized': {
        const t = (raw.title as string | undefined) || (raw.finding_id as string | undefined) || 'New finding';
        title = severity ? `[${severity.toUpperCase()}] ${t}` : t;
        break;
      }
      case 'issue.created':
      case 'issue.updated':
      case 'issue.reopened':
      case 'issue.escalated':
      case 'issue.escalation_fired':
      // R193-SHIP-AN: 6 additional lifecycle verbs. resolve/suppress/snooze
      // are operator actions the FE should always show as a toast.
      // assigned shows the new assignee. severity_changed and
      // priority_changed are gated to P0/P1 only further below — the
      // title is built here regardless because the title is also used in
      // the activity feed even when the toast is suppressed.
      case 'issue.resolved':
      case 'issue.suppressed':
      case 'issue.snoozed':
      case 'issue.assigned':
      case 'issue.severity_changed':
      case 'issue.priority_changed': {
        // R193-SHIP-AM: issue body may sit under raw.issue (process_finding
        // payload) or be flat (status-change webhook envelope). Try both.
        const issueObj = (raw.issue as Record<string, unknown> | undefined) || {};
        const t = (issueObj.title as string | undefined)
          || (raw.title as string | undefined)
          || (issueObj.id as string | undefined)
          || (raw.issue_id as string | undefined)
          || 'Issue update';
        const p = (issueObj.priority as string | undefined) || (raw.priority as string | undefined) || '';
        let verb: string;
        switch (type) {
          case 'issue.reopened': verb = 'Reopened'; break;
          case 'issue.escalated':
          case 'issue.escalation_fired': verb = 'Escalated'; break;
          case 'issue.created': verb = 'New'; break;
          case 'issue.resolved': verb = 'Resolved'; break;
          case 'issue.suppressed': verb = 'Suppressed'; break;
          case 'issue.snoozed': verb = 'Snoozed'; break;
          case 'issue.assigned': {
            // R193-SHIP-AN: assigned toast carries the new assignee inline.
            const newAssignee =
              ((raw.issue as Record<string, unknown> | undefined)?.assigned_to as string | undefined)
              || (raw.assigned_to as string | undefined)
              || (raw.assignee as string | undefined)
              || 'unassigned';
            verb = `Assigned to ${newAssignee}`;
            break;
          }
          case 'issue.severity_changed': {
            const prev = (raw.previous_severity as string | undefined)
              || ((raw.issue as Record<string, unknown> | undefined)?.previous_severity as string | undefined);
            const next = (raw.new_severity as string | undefined)
              || ((raw.issue as Record<string, unknown> | undefined)?.severity as string | undefined);
            verb = `Severity ${prev || '?'} -> ${next || '?'}`;
            break;
          }
          case 'issue.priority_changed': {
            const prev = (raw.previous_priority as string | undefined)
              || ((raw.issue as Record<string, unknown> | undefined)?.previous_priority as string | undefined);
            const next = (raw.new_priority as string | undefined)
              || ((raw.issue as Record<string, unknown> | undefined)?.priority as string | undefined);
            verb = `Priority ${prev || '?'} -> ${next || '?'}`;
            break;
          }
          default: verb = 'Updated';
        }
        title = p ? `${verb} ${p}: ${t}` : `${verb}: ${t}`;
        break;
      }
      case 'scan.started': {
        title = 'Scan started';
        break;
      }
      case 'scan.progress': {
        // Wiz-parity progressive scan: the orchestrator emits this at each
        // milestone (resources_loaded 10% -> rules_evaluated 30% -> modules 50%
        // -> graph 75% -> issues 90% -> agentless vuln). Data views refetch on it
        // so findings/issues/graph STREAM IN during the scan instead of appearing
        // only at scan.completed. Not toasted (App.tsx gates toasts by type).
        const step = (raw.step as string | undefined) || (raw.step_name as string | undefined) || '';
        const pct = (raw.percent as number | undefined) ?? (raw.progress as number | undefined);
        title = `Scan in progress${pct !== undefined ? ` — ${Math.round(pct)}%` : ''}${step ? ` (${step})` : ''}`;
        break;
      }
      case 'scan.completed': {
        const provider = (raw.provider as string | undefined) || '';
        const count = (raw.findings_count as number | undefined) ?? '';
        title = `Scan complete${provider ? ` — ${provider.toUpperCase()}` : ''}${count !== '' ? ` (${count} findings)` : ''}`;
        break;
      }
      case 'toxic_combination.detected':
        title = `⚠ ${(raw.name as string | undefined) || 'Toxic combination'}`;
        break;
      case 'runtime.alert': {
        const threat = (raw.threat_type as string | undefined) || (raw.title as string | undefined) || 'Runtime threat detected';
        const host = (raw.host as string | undefined) || (raw.workload as string | undefined) || '';
        title = severity
          ? `[${severity.toUpperCase()}] Runtime: ${threat}${host ? ` on ${host}` : ''}`
          : `Runtime: ${threat}${host ? ` on ${host}` : ''}`;
        break;
      }
      case 'remediation.completed': {
        const action = (raw.action as string | undefined) || (raw.title as string | undefined) || 'Remediation';
        const target = (raw.resource as string | undefined) || (raw.target as string | undefined) || '';
        title = `Remediation complete: ${action}${target ? ` — ${target}` : ''}`;
        break;
      }
      case 'compliance.change': {
        const framework = (raw.framework as string | undefined) || '';
        const direction = (raw.direction as string | undefined) || (raw.change as string | undefined) || '';
        const score = (raw.score as number | undefined);
        title = `Compliance ${direction || 'changed'}${framework ? ` — ${framework}` : ''}${score !== undefined ? ` (${score}%)` : ''}`;
        break;
      }
      case 'anomaly.detected': {
        const anomalyType = (raw.anomaly_type as string | undefined) || (raw.title as string | undefined) || 'Behavioral anomaly';
        const entity = (raw.entity as string | undefined) || (raw.principal as string | undefined) || '';
        title = severity
          ? `[${severity.toUpperCase()}] Anomaly: ${anomalyType}${entity ? ` — ${entity}` : ''}`
          : `Anomaly: ${anomalyType}${entity ? ` — ${entity}` : ''}`;
        break;
      }
      case 'graph.changed': {
        const entity = (raw.entity_type as string | undefined) || '';
        const action = (raw.action as string | undefined) || '';
        const label  = (raw.label as string | undefined)  || (raw.entity_id as string | undefined) || '';
        title = `Graph ${action}: ${entity}${label ? ` — ${label}` : ''}`;
        break;
      }
      case 'sla.breached': {
        // R193-SHIP-AV: payload shape from issue-engine _sla_breach_checker:
        //   { event, issue_id, tenant_id, title, priority, severity,
        //     sla_deadline, breached_for_seconds }
        const t = (raw.title as string | undefined) || (raw.issue_id as string | undefined) || 'Issue';
        const pri = (raw.priority as string | undefined) || '';
        const sec = (raw.breached_for_seconds as number | undefined);
        let overdueLabel = '';
        if (typeof sec === 'number' && sec > 0) {
          if (sec < 3600) overdueLabel = `${Math.round(sec / 60)}m overdue`;
          else if (sec < 86400) overdueLabel = `${Math.round(sec / 3600)}h overdue`;
          else overdueLabel = `${Math.round(sec / 86400)}d overdue`;
        }
        const prefix = pri ? `SLA breach ${pri}` : 'SLA breach';
        title = overdueLabel ? `${prefix}: ${t} (${overdueLabel})` : `${prefix}: ${t}`;
        // Always treat sla.breached as critical for toast styling regardless
        // of the issue's severity — operators must see this immediately.
        severity = 'critical';
        break;
      }
      default:
        title = type;
    }

    return {
      id: (raw.id as string | undefined) || `${type}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type,
      ts: (raw.ts as string | undefined) || (raw.timestamp as string | undefined) || new Date().toISOString(),
      title,
      severity,
      raw,
    };
  } catch {
    return null;
  }
}

function _readToken(): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
  } catch {
    return '';
  }
}

function _buildWsUrl(): string {
  // R193-SHIP-AM: pass JWT + tenant in the query string so the gateway can
  // tenant-scope the event stream. Custom WS headers aren't supported by
  // the browser WebSocket constructor, so query-string is the only path.
  let base = API_CONFIG.BASE_URL.replace(/^http/, 'ws') + '/ws/events';
  if (typeof window === 'undefined') return base;
  try {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
    let tenant = '';
    try {
      const t = localStorage.getItem('current_tenant');
      if (t) {
        const parsed = JSON.parse(t);
        tenant = parsed?.id || '';
      }
    } catch { /* ignore */ }
    const params: string[] = [];
    if (token) params.push(`token=${encodeURIComponent(token)}`);
    if (tenant) params.push(`tenant=${encodeURIComponent(tenant)}`);
    if (params.length) base += (base.includes('?') ? '&' : '?') + params.join('&');
  } catch { /* localStorage unavailable — fall back to anon */ }
  return base;
}

function _connect() {
  if (_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING)) return;
  // R193-SHIP-AM: don't try to reconnect while tab is hidden. The visibility
  // listener will call _connect when the tab becomes visible again.
  if (_tabHidden) return;
  // R133: on the login screen this module is already loaded, so the singleton
  // used to connect tokenless, get 403'd by the gateway's WS auth, burn all
  // MAX_FAIL_COUNT attempts and drop into the 5-minute idle backoff — with no
  // trigger on login, live updates stayed dead for minutes after signing in.
  // Once we know auth is required, wait for a token instead of retrying.
  if (_awaitingAuth && !_readToken()) return;

  const wsUrl = _buildWsUrl();

  try {
    const ws = new WebSocket(wsUrl);
    _ws = ws;

    ws.onopen = () => {
      _backoff = BASE_BACKOFF_MS;
      _failCount = 0;
      _broadcastConnected(true);
    };

    ws.onmessage = (msg) => {
      // Server-side heartbeat: reply to {"type":"ping"} so the gateway
      // doesn't drop us at WS_HEARTBEAT_TIMEOUT_S.
      try {
        const parsed = JSON.parse(msg.data);
        if (parsed && parsed.type === 'ping' && _ws && _ws.readyState === WebSocket.OPEN) {
          _ws.send(JSON.stringify({ type: 'pong', ts: new Date().toISOString() }));
          return;
        }
      } catch { /* not JSON — fall through to event parsing */ }
      const event = _parseMessage(msg.data);
      if (event) _broadcast(event);
    };

    ws.onclose = () => {
      _ws = null;
      _broadcastConnected(false);
      // R133: a close with no token in storage means the gateway rejected the
      // handshake for auth (it closes before accept, which the browser surfaces
      // as HTTP 403). Retrying can only fail the same way, so stand down and
      // let _onAuthChange wake us when the user signs in.
      if (!_readToken()) {
        _awaitingAuth = true;
        if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
        return;
      }
      _failCount += 1;
      // R193-SHIP-AM: 1s -> 2s -> 5s -> 30s schedule per task spec.
      const scheduled = BACKOFF_SCHEDULE_MS[Math.min(_failCount - 1, BACKOFF_SCHEDULE_MS.length - 1)];
      // After MAX_FAIL_COUNT consecutive failures, slow to IDLE_BACKOFF_MS
      // to avoid console spam when server is truly down for a long time.
      const delay = _failCount >= MAX_FAIL_COUNT ? IDLE_BACKOFF_MS : scheduled;
      if (_failCount === MAX_FAIL_COUNT) {
        console.warn(`[WS] Could not reach ${API_CONFIG.BASE_URL}/ws/events after ${MAX_FAIL_COUNT} attempts — retrying every ${IDLE_BACKOFF_MS / 60000} min`);
      }
      _reconnectTimer = setTimeout(_connect, delay);
      _backoff = Math.min(_backoff * 2, MAX_BACKOFF_MS);
    };

    ws.onerror = () => { ws.close(); };
  } catch {
    // WebSocket not available in test/SSR environments — silently skip
    _failCount += 1;
    const scheduled = BACKOFF_SCHEDULE_MS[Math.min(_failCount - 1, BACKOFF_SCHEDULE_MS.length - 1)];
    _reconnectTimer = setTimeout(_connect, _failCount >= MAX_FAIL_COUNT ? IDLE_BACKOFF_MS : scheduled);
  }
}

// R193-SHIP-AM: Page Visibility — pause WS while tab hidden, reconnect on resume.
// Wiz pushes critical issues to the visible tab; a background tab piling up
// reconnect attempts wastes battery and floods server logs on resume.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      _tabHidden = true;
      if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
      if (_ws && _ws.readyState === WebSocket.OPEN) {
        try { _ws.close(1000, 'tab_hidden'); } catch { /* ignore */ }
      }
    } else {
      _tabHidden = false;
      // Reset backoff on user-initiated resume so we reconnect promptly.
      _failCount = 0;
      _backoff = BASE_BACKOFF_MS;
      _connect();
    }
  });
}

// R133: reconnect the moment authentication changes. Wiz streams live the
// instant you land in the console; here the singleton had connected (and been
// 403'd) back on the login screen, and nothing rewoke it after sign-in.
// AuthContext dispatches `auth:token` whenever it stores or clears the token;
// the `storage` event covers sign-in/out performed in another tab.
function _onAuthChange() {
  _awaitingAuth = false;
  _failCount = 0;
  _backoff = BASE_BACKOFF_MS;
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
  if (_readToken()) {
    // Drop a socket opened under the previous identity so the gateway
    // re-scopes the stream to the new tenant.
    if (_ws) { try { _ws.close(1000, 'auth_changed'); } catch { /* ignore */ } _ws = null; }
    _connect();
  } else if (_ws) {
    try { _ws.close(1000, 'signed_out'); } catch { /* ignore */ }
    _ws = null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('auth:token', _onAuthChange);
  window.addEventListener('storage', (e) => {
    if (e.key === 'access_token') _onAuthChange();
  });
}

// Probe the HTTP health endpoint before opening WebSocket.
// This prevents browser-native "WebSocket connection failed" noise when the
// API gateway isn't running yet (WebSocket errors cannot be suppressed in JS).
let _probeTimer: ReturnType<typeof setTimeout> | null = null;

async function _probeAndConnect() {
  // R133: this runs at module load — i.e. while the login screen is showing,
  // before any token exists. Connecting then can only be rejected by the
  // gateway's WS auth, so don't: _onAuthChange fires the moment AuthContext
  // stores a token (including the dev auto-login path) and connects us.
  if (!_readToken()) {
    _awaitingAuth = true;
    return;
  }
  try {
    const { status } = await apiClient.request<unknown>('/health', {
      method: 'GET',
      timeoutMs: 2000,
      retry: false,
    });
    if (status > 0 && status < 500) {
      _connect(); // server is up — attempt WebSocket
      return;
    }
  } catch {
  }
  // Server not reachable — retry health probe silently (no WebSocket attempt)
  _probeTimer = setTimeout(_probeAndConnect, 5000);
}

if (typeof window !== 'undefined') {
  _probeAndConnect();
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseSecurityEventsResult {
  events: SecurityEvent[];
  latest: SecurityEvent | null;
  isConnected: boolean;
  clear: () => void;
}

export function useSecurityEvents(): UseSecurityEventsResult {
  const [events, setEvents] = useState<SecurityEvent[]>(() => [..._eventBuffer]);
  const [latest, setLatest] = useState<SecurityEvent | null>(_eventBuffer[0] ?? null);
  const [isConnected, setIsConnected] = useState(_isConnected);

  const clear = useCallback(() => {
    _eventBuffer.length = 0;
    setEvents([]);
    setLatest(null);
  }, []);

  useEffect(() => {
    const onEvent = (ev: SecurityEvent) => {
      setLatest(ev);
      setEvents([...(_eventBuffer)]);
    };
    const onConnected = (connected: boolean) => setIsConnected(connected);

    _eventListeners.add(onEvent);
    _connectedListeners.add(onConnected);

    // Sync with current state in case it changed before mount
    setIsConnected(_isConnected);
    setEvents([..._eventBuffer]);

    return () => {
      _eventListeners.delete(onEvent);
      _connectedListeners.delete(onConnected);
    };
  }, []);

  return { events, latest, isConnected, clear };
}

/**
 * R193-SHIP-AN: debounced graph.changed refresh hook.
 *
 * graph.changed events fire often — a single periodic graph-sync tick can emit
 * 50+ node-modified deltas in a 10-second window, and per-tenant polling
 * multiplies that further. Consumers that re-fetch their view on every event
 * would hammer the BE. This hook collapses a burst of `graph.changed` events
 * into a single trailing-edge callback after `debounceMs` of quiet.
 *
 * Usage:
 *   useGraphChangedRefresh(() => refetch(), 1000);
 *
 * The callback fires `debounceMs` after the LAST graph.changed event in a
 * burst. If the component unmounts before the timer fires, the pending call
 * is cancelled. Default debounce is 1000ms per R193-SHIP-AN task spec.
 */
export function useGraphChangedRefresh(
  callback: (latestEvent: SecurityEvent) => void,
  debounceMs: number = 1000,
): void {
  const { latest } = useSecurityEvents();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  // Keep callback ref current so consumers can pass an inline lambda without
  // resetting the debounce timer on every render.
  useEffect(() => { callbackRef.current = callback; }, [callback]);

  useEffect(() => {
    if (!latest || latest.type !== 'graph.changed') return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const ev = latest;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      try { callbackRef.current(ev); } catch { /* swallow — never let a consumer crash the hook */ }
    }, Math.max(0, debounceMs));
  }, [latest, debounceMs]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
}
